import React, { useEffect, useRef, useState } from "react";

import { getResiduePayoutPotential } from "services";

import { scrollIntoViewWithObserver } from "utils";

import { CropExplorationResponse, Recommendation, RecommendedCrop } from "models";

import { Popover } from "components";

import { RecommendationCard } from "pages/app/crop-explorer/components/recommendation-card/RecommendationCard.tsx";
import { RecommendationCardForm } from "pages/app/crop-explorer/components/recommendation-card/RecommendationCardForm.tsx";

type RecommendationCardPopoverProps = {
  recommendation: Recommendation;
  cropExploration: CropExplorationResponse;
};

export type RecommendedCropsWithResiduePayoutPotentials = RecommendedCrop & { residuePayoutPotential?: number };

export type RecommendationsWithResiduePayoutPotentials = {
  year: number;
  crops: RecommendedCropsWithResiduePayoutPotentials[];
  basedOnYears: number[];
};

export const RecommendationCardPopover = ({ recommendation, cropExploration }: RecommendationCardPopoverProps) => {
  const recommendationCardRef = useRef<HTMLButtonElement | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [recommendationsWithResiduePayoutPotentials, setRecommendationsWithResiduePayoutPotentials] =
    useState<RecommendationsWithResiduePayoutPotentials>(recommendation);

  const handleAfterAddOrDismiss = () => {
    setShowModal(false);
  };

  const loadAllResiduePayoutPotentials = () =>
    recommendation.crops.map(async (crop) => {
      if (!crop.residue) return { ...crop };
      const residuePayoutPotential = await getResiduePayoutPotential({
        cropId: crop.cropId,
        hectares: cropExploration.hectares,
        regionId: cropExploration.regionId,
      });
      return { ...crop, residuePayoutPotential: residuePayoutPotential.residuePayoutAvg };
    });

  useEffect(() => {
    const startTime = Date.now();
    Promise.all(loadAllResiduePayoutPotentials()).then((cropWithResiduePayoutPotentials) => {
      setRecommendationsWithResiduePayoutPotentials({ ...recommendation, crops: cropWithResiduePayoutPotentials });
      const endTime = Date.now();
      const dif = startTime - endTime;
      if (dif > 2000) {
        setIsLoading(false);
        return;
      }
      setTimeout(() => setIsLoading(false), 2000 - dif);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Popover
      className="recommendation-card-popover"
      content={
        <RecommendationCardForm
          recommendation={recommendationsWithResiduePayoutPotentials}
          cropExploration={cropExploration}
          handleAfterAddOrDismiss={handleAfterAddOrDismiss}
          onClose={() => setShowModal(false)}
        />
      }
      dataTestId={`recommendation-card-${recommendation.year}-modal`}
      trigger="click"
      placement="left"
      open={showModal}
      onOpenChange={async () => {
        // the order is important for the  unit test
        setShowModal(!showModal);
        await scrollIntoViewWithObserver(recommendationCardRef);
      }}
      destroyOnHidden={true}
    >
      <button style={{ background: "none", border: "none", padding: 0 }} disabled={isLoading} ref={recommendationCardRef}>
        <RecommendationCard year={recommendation.year} isActive={showModal} isLoading={isLoading} />
      </button>
    </Popover>
  );
};
